import { MutableRefObject, useCallback, useEffect, useState } from 'react'
import ReactQuill from 'react-quill'
import { useDispatch } from 'react-redux'

import { useSnippets } from '../../../hooks/_useSnippets'
import { storage } from '../../../lib/storage'
import { updateDictationReducer } from '../../../redux/reducers/visits-slice'
import { visitsService } from '../../../services/http/visits.service'
import { useLocalContext } from '../../VisitDictationShared/hooks/useLocalContext'
import { useQuillEditor } from '../../VisitDictationShared/hooks/useQuillEditor'

type HandleStatus = (status: 'saved' | 'unsaved' | 'saving') => void
interface Props {
    dictationId: string
    dictationContent: string
    handleStatus: HandleStatus
    quillRef: MutableRefObject<ReactQuill | null>
}

export const useDictationFunctions = ({ dictationId, dictationContent, handleStatus, quillRef }: Props) => {
    const [autosaveDictation, setAutosaveDictation] = useState(false)
    const [networkIssue, setNetworkIssue] = useState(false)
    const [snippetCheck, setSnippetCheck] = useState(false)
    const { applySnippetInDictation } = useSnippets()
    const { duration } = useLocalContext().timer

    // Transcriber actions
    const { removeTextBackwards, addText } = useQuillEditor({ quillRef })

    const dispatch = useDispatch()

    const handleBeforeUnload = useCallback(() => {
        // Check dictation in localstorage
        const dictation = storage.getDictation(dictationId)

        // If saved then remove dictation
        if (dictation?.status === 'saved') storage.clearSavedDictations()
    }, [dictationId])

    const handleContentChange = useCallback(
        (content: string) => {

            if (!snippetCheck) {
                const finalSnippetContent = applySnippetInDictation(content)

                if (finalSnippetContent) {
                    // Remove text backwards
                    removeTextBackwards(finalSnippetContent.abbrLength)

                    // Add snippet check
                    setSnippetCheck(true)

                    // Add text
                    addText(finalSnippetContent.snippet + ' ')
                    return
                }
            }

            // Reset snippet check
            setSnippetCheck(false)

            // Change content in redux state
            dispatch(updateDictationReducer({ _id: dictationId, dictation: content }))

            // Save to local storage
            storage.storeDictation(dictationId, content)

            // Update status
            handleStatus('unsaved')

            // Call backend to update dictation content
            setAutosaveDictation(true)
        },
        [dictationId, dispatch, handleStatus, applySnippetInDictation, removeTextBackwards, addText, snippetCheck]
        // [dictationId, dispatch, handleStatus, applySnippetInDictation, addText, snippetCheck]
    )

    const updateDictationOnDB = useCallback(async () => {
        await visitsService.updateVisit(dictationId, { dictation: dictationContent, duration })
        storage.updateDictationStatus(dictationId, 'saved')
        handleStatus('saved')
    }, [dictationContent, dictationId, handleStatus, duration])

    useEffect(() => {
        // == Handle autoSave == //
        if (!autosaveDictation) return
        const interval = setInterval(
            async () => {
                try {
                    handleStatus('saving')
                    setAutosaveDictation(false)
                    setNetworkIssue(false)
                    await updateDictationOnDB()
                } catch (error) {
                    handleStatus('unsaved')
                    storage.updateDictationStatus(dictationId, 'unsaved')
                    setAutosaveDictation(true)
                    setNetworkIssue(true)
                }
            },
            !networkIssue ? 1000 : 10000
        )

        return () => clearInterval(interval)
    }, [autosaveDictation, dictationId, dictationContent, networkIssue, handleStatus, updateDictationOnDB])

    useEffect(() => {
        // == Handle storage before unload == //
        // Check status in local storage
        const dictation = storage.getDictation(dictationId)

        // Save unsaved content
        if (dictation?.status === 'unsaved') {
            handleContentChange(dictation.content)
        }

        // If status === 'saved' remove from local storage
        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            // Clean up the event listener
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [dispatch, handleBeforeUnload, dictationId, handleContentChange])

    return {
        handleContentChange,
        handleBeforeUnload
    }
}
